import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { FeeGroupEnum } from '../../models/fee-groupings/fee-groupings.model';

import { StatementProcessorEnum } from '../../models/statement/statement.model';
import { DocumentPreviewComponent } from '../document-preview/document-preview.component';
import { ClassifySelectionResult } from './document-text-select.models';

@Injectable({
    providedIn: 'root'
})
export class DocumentTextSelectService implements OnDestroy {

    private determinedProcessor: string;
    private processorFormat: string;
    private isChaseF2Format: boolean;

    private MAX_SELECTION_LENGTH = 10000;

    constructor(
        private elementRef: ElementRef,
        private mspc: DocumentPreviewComponent,
        private isAI: boolean
    ) {
        this.addEventListeners(this.elementRef.nativeElement);
    }

    addEventListeners(element): void {
        element.addEventListener('mouseup', this.getSelectedText, false);

        // prevent other double clicks so classify selected fees will work better
        // (The handler is executed in the capturing phase.)
        element.addEventListener('dblclick', this.processDblClick, true);
    }

    ngOnDestroy(): void {
        this.elementRef.nativeElement.removeEventListener('mouseup', this.getSelectedText, false);
        this.elementRef.nativeElement.removeEventListener('dblclick', this.processDblClick, false);
    }

    // called from manual-statement-processing.component.ts
    setProcessorAndFormat(firstPageDivs) {
        const { determinedProcessor, processorFormat } = this.determineProcessorAndFormat(firstPageDivs);
        this.determinedProcessor = determinedProcessor;
        this.processorFormat = processorFormat;
        this.isChaseF2Format = this.determinedProcessor === StatementProcessorEnum.PAYMENTECH && this.processorFormat === 'F2';
    }

    // check if format needs to be handled in a special way
    determineProcessorAndFormat(firstPageDivs) {
        if (firstPageDivs.length > 0) {
            // CHASE paymentech 2
            if (firstPageDivs.find(div => div.el.textContent.includes('4 Northeastern Boulevard, Salem, NH 03079-1952'))) {
                return {
                    determinedProcessor: StatementProcessorEnum.PAYMENTECH,
                    processorFormat: 'F2'
                };
            }

            return {
                determinedProcessor: StatementProcessorEnum.UNKNOWN,
                processorFormat: 'INCOMPLETE'
            };
        } else {
            return {
                determinedProcessor: 'UNREADABLE',
                processorFormat: 'UNREADABLE'
            };
        }
    }

    getStartingElement(firstElement): [Element, number] {
        let countFromFirstElementToPreviousLineBreak = 0;
        const recGetStartingElement = (curElement): Element => {
            const previousElement = curElement.previousElementSibling;
            if (!previousElement)
                return curElement;
            if (previousElement.tagName === 'BR') {
                return curElement;
            }
            if (this.getPxNumber(curElement.style.left) < this.getPxNumber(previousElement.style.left)) {
                return curElement;
            }
            if (this.getPxNumber(curElement.style.top) !== this.getPxNumber(previousElement.style.top)) {
                return curElement;
            }
            countFromFirstElementToPreviousLineBreak++;
            return recGetStartingElement(previousElement);
        };

        const recGetStartingElementChaseF2 = (curElement, midLineBreakReached = false): Element => {
            const previousElement = curElement.previousElementSibling;
            if (!previousElement)
                return curElement;
            if (previousElement.tagName === 'BR') {
                return curElement;
            }
            countFromFirstElementToPreviousLineBreak++;
            if (this.getPxNumber(curElement.style.left) < this.getPxNumber(previousElement.style.left)) {
                return recGetStartingElementChaseF2(previousElement, true);
            }
            if (this.getPxNumber(curElement.style.top) !== this.getPxNumber(previousElement.style.top)) {
                return curElement;
            }
            return recGetStartingElementChaseF2(previousElement, midLineBreakReached);
        };

        return this.isChaseF2Format
            ? [recGetStartingElementChaseF2(firstElement), countFromFirstElementToPreviousLineBreak]
            : [recGetStartingElement(firstElement), countFromFirstElementToPreviousLineBreak];
    }

    getSelectedNodes(documentSelection): [Element[], number, number] {
        const rangeResult = this.getSelectedNodesFromRange(documentSelection);
        if (rangeResult) {
            return rangeResult;
        }
        const elementAnchor: Element = documentSelection.anchorNode.nodeName === '#text'
            ? documentSelection.anchorNode.parentNode
            : documentSelection.anchorNode;

        const elementFocus: Element = documentSelection.focusNode.nodeName === '#text'
            ? documentSelection.focusNode.parentNode
            : documentSelection.focusNode;

        const positionAnchor = elementAnchor.compareDocumentPosition(elementFocus)
        const firstElement = (positionAnchor & Node.DOCUMENT_POSITION_FOLLOWING) || positionAnchor === 0 ? elementAnchor : elementFocus;
        const lastElement = firstElement === elementAnchor ? elementFocus : elementAnchor;

        // Check for <br> tags and left px location to detect line breaks
        const [startingElement, startingNodeOffset] = this.getStartingElement(firstElement);
        // We want to be grabbing the element itself, not the contained
        // text
        const endingElement = lastElement;
        // allows assignment of columns besides the last one
        let countFromLastElementToLineBreak = -1;

        // recursively builds list of sibling nodes by following sibling chain
        const recGetSelectedNodes = (curElement, selectedElements, depth = 0): [Element[], number] => {
            if (depth >= this.MAX_SELECTION_LENGTH) {
                return [selectedElements, countFromLastElementToLineBreak >= 0 ? countFromLastElementToLineBreak : 0];
            }
            if (curElement === endingElement) {
                countFromLastElementToLineBreak = 0;
            } else if (countFromLastElementToLineBreak >= 0) {
                countFromLastElementToLineBreak++;
            }
            const last = selectedElements[selectedElements.length - 1];
            if (countFromLastElementToLineBreak >= 0) {
                if (!curElement || curElement.tagName === 'BR') {
                    return [[...selectedElements, curElement], countFromLastElementToLineBreak - 1];
                }
                const leftWentBack = this.getPxNumber(curElement.style.left) < this.getPxNumber(last.style.left);
                const topChanged = this.getPxNumber(curElement.style.top) !== this.getPxNumber(last.style.top);
                if (leftWentBack || topChanged) {
                    if (last.textContent.trim().length === 0 && countFromLastElementToLineBreak === 0) {
                        return [selectedElements, countFromLastElementToLineBreak + 1];
                    }
                    if (endingElement.textContent.trim().length === 0) {
                        return [selectedElements, countFromLastElementToLineBreak];
                    }
                    return [selectedElements, countFromLastElementToLineBreak - 1];
                }
            }

            if (this.isAI && curElement.nextElementSibling === null && countFromLastElementToLineBreak >= 0) {
                selectedElements.push(curElement);
                return [selectedElements, countFromLastElementToLineBreak];
            }

            const isEndOfAiLine = this.isAI && curElement.nextElementSibling === null;

            let nextElement;
            if (isEndOfAiLine) {
                const nextParentElement = curElement.parentNode?.nextElementSibling;

                nextElement = nextParentElement
                    ? (nextParentElement.children[0]?.textContent?.trim().length === 0
                        ? nextParentElement.nextElementSibling?.children[0]
                        : nextParentElement.children[0])
                    : null;

            } else {
                // TODO (jacob): Handle case where curElement is null
                nextElement = curElement?.nextElementSibling ?? null;
            }

            if (nextElement == null && countFromLastElementToLineBreak < 0) {
                return [this.truncateToFirstLine(selectedElements), 0];
            }
            if (isEndOfAiLine)
                return recGetSelectedNodes(nextElement, [...selectedElements, curElement, document.createElement("br")], depth + 1);

            return recGetSelectedNodes(nextElement, [...selectedElements, curElement], depth + 1);
        };

        const recGetSelectedNodesChaseF2 = (curElement, selectedElements, depth = 0): [Element[], number] => {
            if (depth >= this.MAX_SELECTION_LENGTH) {
                return [selectedElements, countFromLastElementToLineBreak >= 0 ? countFromLastElementToLineBreak : 0];
            }
            if (curElement === endingElement) {
                countFromLastElementToLineBreak = 0;
            } else if (countFromLastElementToLineBreak >= 0) {
                countFromLastElementToLineBreak++;
            }

            const nextElement = curElement?.nextElementSibling ?? null;

            if (nextElement == null && countFromLastElementToLineBreak < 0) {
                return [this.truncateToFirstLine(selectedElements), 0];
            }
            if (countFromLastElementToLineBreak >= 0) {
                if (!curElement || curElement.tagName === 'BR') {
                    return [selectedElements, countFromLastElementToLineBreak];
                }
            }

            return recGetSelectedNodesChaseF2(nextElement, [...selectedElements, curElement], depth + 1);
        };

        const [selectedNodes, endingNodeOffset]: [Element[], number] = this.isChaseF2Format
            ? recGetSelectedNodesChaseF2(startingElement, [], 0)
            : recGetSelectedNodes(startingElement, [], 0);

        return [selectedNodes, startingNodeOffset, endingNodeOffset];
    }

    getSelectedNodeFullLine(documentSelection): [Element[], number, number] {
        const nodeInLine = documentSelection.anchorNode;
        const elementInLine = nodeInLine.nodeName === '#text'
            ? nodeInLine.parentNode
            : nodeInLine;

        const [startingElement, startingNodeOffset] = this.getStartingElement(elementInLine);
        const recGetFullLine = (curElement, selectedElements, depth = 0): [Element[], number] => {
            if (depth >= this.MAX_SELECTION_LENGTH) {
                return [selectedElements, 0];
            }

            const last = selectedElements[selectedElements.length - 1];
            if (!curElement || curElement.tagName === 'BR') {
                return [[...selectedElements], 0];
            }
            if (last) {
                const curLeft = this.getPxNumber(curElement.style.left);
                const lastLeft = this.getPxNumber(last.style.left);
                const curTop = this.getPxNumber(curElement.style.top);
                const lastTop = this.getPxNumber(last.style.top);
                if (curLeft < lastLeft || curTop !== lastTop) {
                    return [selectedElements, 1];
                }
            }

            if (this.isAI && curElement.nextElementSibling === null) {
                selectedElements.push(curElement);
                return [selectedElements, 0];
            }

            const isEndOfAiLine = this.isAI && curElement.nextElementSibling === null;

            const nextElement = curElement?.nextElementSibling ?? null;
            if (nextElement == null) {
                return [[...selectedElements, curElement], 0];
            }

            if (isEndOfAiLine)
                return [[...selectedElements, curElement, document.createElement("br")], 0];

            return recGetFullLine(nextElement, [...selectedElements, curElement], depth + 1);
        };

        const [selectedNodes, endingNodeOffset]: [Element[], number] = recGetFullLine(startingElement, [], 0);
        return [selectedNodes, startingNodeOffset, endingNodeOffset];
    }

    getPxNumber(px: string): number {
        if (px == null || px === '') {
            return 0;
        }
        const pxNumber = parseFloat(String(px).replace('px', ''));
        return Number.isNaN(pxNumber) ? 0 : pxNumber;
    }

    formatAmtColumn(amount: string): number {
        // replace left parenthesis with a minus sign
        amount = amount.replace(/[(]/g, '-');
        // remove dollar signs and right parentheses from fee amount
        amount = amount.replace(/[$,),]/g, '');
        // return the amount as a float
        return Math.round(parseFloat(amount) * 100) / 100;
    }

    getIndexOfFirstSpan = (list: Element[]) => list.findIndex(element => element.tagName === 'SPAN');

    parseSelectedNodes(selectedNodes: Element[], startingNodeOffset: number, endingNodeOffset: number): ClassifySelectionResult[] {
        // recursivey builds list rows of elements
        const recParseSelectedNodes = (currentLineOfElements, selectedElements): HTMLElement[][] => {
            if (selectedElements.length === 0) {
                return [currentLineOfElements];
            }

            const [first, ...rest] = selectedElements;
            if (first.tagName === 'BR') {
                if (rest.length === 0)
                    return [currentLineOfElements];
                if (rest[0].tagName === 'BR')
                    return [currentLineOfElements, ...recParseSelectedNodes([], rest.slice(this.getIndexOfFirstSpan(rest)))];
                if (rest.length >= 2 && currentLineOfElements.length >= 1
                    && (this.isAI || this.getPxNumber(rest[0].style.left) < this.getPxNumber(currentLineOfElements[currentLineOfElements.length - 1].style.left))) {
                    return [currentLineOfElements, ...recParseSelectedNodes([], rest)];
                }
            }
            if (currentLineOfElements.length > 0
                && this.getPxNumber(first.style.left) < this.getPxNumber(currentLineOfElements[currentLineOfElements.length - 1].style.left)) {
                return [currentLineOfElements, ...recParseSelectedNodes([], selectedElements)];
            }

            return recParseSelectedNodes([...currentLineOfElements, first], rest);
        };

        // in this format, the first and last elements are on a span line by themselves,
        // then the rest of the line (middle cols), then a BR (signifies actual end of line in this format)
        const recParseSelectedNodesChaseF2 = (currentLineOfElements, selectedElements): HTMLElement[][] => {
            // last col is copied to the end of each line
            const adjustedLine = [...currentLineOfElements, currentLineOfElements[2]];

            if (selectedElements.length === 0) {
                return [adjustedLine];
            }

            const [first, ...rest] = selectedElements;
            if (first.tagName === 'BR') { // dont use span style for linebreaks
                if (rest.length === 0)
                    return [adjustedLine];
                if (this.getPxNumber(rest[1].style.left) < this.getPxNumber(currentLineOfElements[currentLineOfElements.length - 1].style.left)) {
                    return [adjustedLine, ...recParseSelectedNodesChaseF2([], rest)];
                }
                return recParseSelectedNodesChaseF2(adjustedLine, rest);
            }

            if (currentLineOfElements.length > 0
                && this.getPxNumber(first.style.left) < this.getPxNumber(currentLineOfElements[currentLineOfElements.length - 1].style.left)) {
                return recParseSelectedNodesChaseF2([...currentLineOfElements, first], rest);
            }

            return recParseSelectedNodesChaseF2([...currentLineOfElements, first], rest);
        };

        // lines that have one br in the middle are usually '1 element' lines hiding inside the next line
        const breakUpLinesWithOneBr = (selectedNodes) => {
            return selectedNodes.reduce((acc, selectedNodeLine) => {
                const indexOfBr = selectedNodeLine.findIndex(node => node.tagName === 'BR');
                if (indexOfBr !== selectedNodeLine.length - 1
                    && selectedNodeLine.filter(node => node.tagName === 'BR').length === 1) {
                    const newLine1 = selectedNodeLine.slice(0, indexOfBr);
                    const newLine2 = selectedNodeLine.slice(indexOfBr + 1);
                    acc.push(newLine1);
                    acc.push(newLine2);
                    return acc;
                }
                acc.push(selectedNodeLine);
                return acc;
            }, []);
        };

        const parsedNodes = this.isChaseF2Format
            ? recParseSelectedNodesChaseF2([], selectedNodes)
            : breakUpLinesWithOneBr(recParseSelectedNodes([], selectedNodes));

        let longestLine = parsedNodes[0];
        for (const line of parsedNodes) {
            if (line.length > longestLine.length)
                longestLine = line;
        }

        // contains benchmarks for where the cells should be
        const styleLeftBenchmarks = longestLine.map(cell => this.getPxNumber(cell.style.left));

        // contains benchmarks for where the cells' right location in px should be
        // based off of maxWidth (same as minWidth) and margin left attributes because
        // left style attribute isnt present in ai side
        let lengthSoFar = 0;

        const aiStyleRightBenchmarks = longestLine.map(cell => {
            const cellLength = this.getPxNumber(cell.style.marginLeft) + this.getPxNumber(cell.style.maxWidth);
            lengthSoFar += cellLength;
            return lengthSoFar;
        });

        // recursively add nulls to one line for all missing cells by matching up to style benchmarks
        const recAddNullsForBlankCells = (styleBenchmarks, curLine) => {
            if (curLine.length === 0 && styleBenchmarks.length === 0)
                return [];
            if (curLine.length === 0 && styleBenchmarks.length > 0)
                return Array(styleBenchmarks.length).fill(null);
            const [head, ...tail] = curLine;
            const [curBenchmark, ...restStyles] = styleBenchmarks;
            if (head !== null && styleBenchmarks.length > 1
                && Math.abs(this.getPxNumber(head.style.left) - curBenchmark) > Math.abs(this.getPxNumber(head.style.left) - styleBenchmarks[1]))
                return [null, ...recAddNullsForBlankCells(restStyles, curLine)];
            return [head, ...recAddNullsForBlankCells(restStyles, tail)];
        };

        // recursively add nulls to one line for all missing cells by matching up to style benchmarks
        const recAiAddNullsForBlankCells = (styleBenchmarks, curLine, lineLengthSoFar) => {
            if (curLine.length === 0 && styleBenchmarks.length === 0)
                return [];
            if (curLine.length === 0 && styleBenchmarks.length > 0)
                return Array(styleBenchmarks.length).fill(null);
            const [head, ...tail] = curLine;
            const [curBenchmark, ...restStyles] = styleBenchmarks;
            const lengthOfHead = this.getPxNumber(head.style.marginLeft) + this.getPxNumber(head.style.maxWidth);
            const rightPxHeadLocation = lineLengthSoFar + lengthOfHead;
            if (head !== null && styleBenchmarks.length > 1
                && Math.abs(rightPxHeadLocation - curBenchmark) > Math.abs(rightPxHeadLocation - styleBenchmarks[1]))
                return [null, ...recAiAddNullsForBlankCells(restStyles, curLine, lineLengthSoFar + lengthOfHead)];
            return [head, ...recAiAddNullsForBlankCells(restStyles, tail, lineLengthSoFar + lengthOfHead)];
        };

        // TODO: figure out why this sometimes has rows that are not equal length. Why does a null get added twice sometimes?
        const parsedAndAdjustedNodes = parsedNodes.map((lineOfElements) => {
            if (!this.isAI && lineOfElements.length !== styleLeftBenchmarks.length)
                return recAddNullsForBlankCells(styleLeftBenchmarks, lineOfElements);
            else return recAiAddNullsForBlankCells(aiStyleRightBenchmarks, lineOfElements, 0);
        });

        // count back on last line <endingNodeOffset> number of times, skipping nulls
        const recFindAdjustedEndingIndex = (endingNodeOffset, adjustedEndingNodeOffset, restLastLine) => {
            if (restLastLine.length === 0)
                return adjustedEndingNodeOffset;
            const lastElement = restLastLine[restLastLine.length - 1];
            const rest = restLastLine.slice(0, restLastLine.length - 1);
            if (lastElement === null) {
                return recFindAdjustedEndingIndex(endingNodeOffset, adjustedEndingNodeOffset + 1, rest);
            }
            if (endingNodeOffset <= 0)
                return adjustedEndingNodeOffset;
            if (restLastLine.length === 0 || endingNodeOffset === 0) {
                return adjustedEndingNodeOffset;
            }
            return recFindAdjustedEndingIndex(endingNodeOffset - 1, adjustedEndingNodeOffset + 1, rest);
        };

        const adjustedEndingNodeOffset = recFindAdjustedEndingIndex(endingNodeOffset, 0, parsedAndAdjustedNodes[parsedAndAdjustedNodes.length - 1]);

        // count in to first line <startingNodeOffset> number of times, skipping nulls
        const recFindAdjustedStartingIndex = (startNodeOffset, adjustedStartingNodeOffset, restFirstLine) => {
            const [head, ...rest] = restFirstLine;
            if (head === null) {
                return recFindAdjustedStartingIndex(startNodeOffset, adjustedStartingNodeOffset + 1, rest);
            }
            if (restFirstLine.length === 0 || startNodeOffset === 0) {
                return adjustedStartingNodeOffset;
            }
            return recFindAdjustedStartingIndex(startNodeOffset - 1, adjustedStartingNodeOffset + 1, rest);
        };

        const adjustedStartingNodeOffset = recFindAdjustedStartingIndex(startingNodeOffset, 0, parsedAndAdjustedNodes[0]);

        return parsedAndAdjustedNodes.map((lineOfElements, index) => {
            const amount = lineOfElements[lineOfElements.length - 1 - adjustedEndingNodeOffset] === null
                ? "$0"
                : lineOfElements[lineOfElements.length - 1 - adjustedEndingNodeOffset].textContent;

            return {
                position: index,
                optionSelected: false,
                feeLabel: lineOfElements[adjustedStartingNodeOffset] === null ? '' : lineOfElements[adjustedStartingNodeOffset].textContent,
                feeAmount: this.formatAmtColumn(amount),
                feeGroup: FeeGroupEnum.INTERCHANGE,
                isFlagged: false,
                htmlElements: this.isChaseF2Format
                    ? lineOfElements.filter(a => a !== null).slice(0, lineOfElements.length - 1)
                    : lineOfElements.filter(a => a !== null)
            };
        });
    }

    getSelectedText = (): void => { // arrow function needed to preserve the 'this' keyword
        const selectedDataObj = document.getSelection();
        if (selectedDataObj?.toString().length === 0)
            return;

        const [selectedNodes, startingNodeOffset, endingNodeOffset]: [Element[], number, number] =
            selectedDataObj?.anchorNode === selectedDataObj?.focusNode
                ? this.getSelectedNodeFullLine(selectedDataObj)
                : this.getSelectedNodes(selectedDataObj);

        const selectedNodesNoNulls = selectedNodes.filter(a => a);
        const selectedData: ClassifySelectionResult[] = this.parseSelectedNodes(selectedNodesNoNulls, startingNodeOffset, endingNodeOffset);
        this.mspc.openClassifyPdfFees(selectedData);
    };

    // the prevents zooming on the pdf side and interferring with document selection. also allows double click on a text row and using it for the popup dialog for single row selects.
    processDblClick(event: Event) {
        event.preventDefault();
        event.stopPropagation();
    }

    private truncateToFirstLine = (elements: Element[]): Element[] => {
        if (elements.length <= 1) {
            return elements;
        }
        for (let i = 1; i < elements.length; i++) {
            const currentElement = elements[i] as HTMLElement;
            const previousElement = elements[i - 1] as HTMLElement;
            if (!currentElement?.style || !previousElement?.style) continue;
            const curLeft = this.getPxNumber(currentElement.style.left);
            const prevLeft = this.getPxNumber(previousElement.style.left);
            const curTop = this.getPxNumber(currentElement.style.top);
            const prevTop = this.getPxNumber(previousElement.style.top);
            const leftWentBack = curLeft < prevLeft;
            const topChanged = curTop !== prevTop;
            if (leftWentBack || topChanged) {
                return elements.slice(0, i);
            }
        }
        return elements;
    };

    /**
     * Uses the Selection Range to get span elements that intersect the selection,
     * then restricts to the single row (same top) as the selection start.
     * Returns null if we should fall back to the sibling-walk logic.
     */
    private getSelectedNodesFromRange(documentSelection: Selection): [Element[], number, number] | null {
        if (!documentSelection.rangeCount) return null;
        const range = documentSelection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const root = container.nodeType === Node.ELEMENT_NODE ? container as Element : container.parentElement;
        if (!root) return null;
        const spans: Element[] = [];
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
        let node: Element | null;
        while (node = walker.nextNode() as Element | null) {
            if (node.nodeName === 'SPAN' && range.intersectsNode(node)) {
                spans.push(node);
            }
        }
        if (spans.length === 0) return null;
        spans.sort((a, b) => {
            const topA = this.getPxNumber((a as HTMLElement).style?.top);
            const topB = this.getPxNumber((b as HTMLElement).style?.top);
            if (topA !== topB) return topA - topB;
            const leftA = this.getPxNumber((a as HTMLElement).style?.left);
            const leftB = this.getPxNumber((b as HTMLElement).style?.left);
            if (leftA !== leftB) return leftA - leftB;
            const pos = a.compareDocumentPosition(b);
            return (pos & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1;
        });
        return [spans, 0, 0];

    }
}
